import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { AuthService } from '../auth/auth.service';
import { ThemeService } from '../theme/theme.service';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { CASTOR_API_URL } from '../api-config';
import { LanguageService } from '../i18n/language.service';
import { I18nService } from '../i18n/i18n.service';
import { TranslatePipe } from '../i18n/translate.pipe';
import { CastorCrud } from '../crud-builder/cruds.service';
import { CustomerLookupService } from '../customers/customer-lookup.service';
import { MarketplaceService } from '../marketplace/marketplace.service';

@Component({
  selector: 'app-main-layout',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive, RouterOutlet, TranslatePipe],
  templateUrl: './main-layout.component.html',
  styleUrls: ['./main-layout.component.css'],
})
export class MainLayoutComponent {
  private static readonly ADMIN_MODE_KEY = 'castor.runner.adminMode';

  private auth = inject(AuthService);
  private http = inject(HttpClient);
  private router = inject(Router);
  private customerLookup = inject(CustomerLookupService);
  private marketplace = inject(MarketplaceService);
  readonly themeService = inject(ThemeService);
  readonly languageService = inject(LanguageService);
  readonly i18n = inject(I18nService);
  readonly customerName = signal<string>('');
  readonly adminMode = signal<boolean>(true);
  readonly formsForMenu = signal<CastorCrud[]>([]);

  constructor() {
    this.loadCustomerName();
    this.loadAdminMode();
    this.loadFormsForMenuIfNeeded();
  }

  private async loadCustomerName(): Promise<void> {
    try {
      const cachedRaw = sessionStorage.getItem('castor.runner.customer');
      if (cachedRaw) {
        try {
          const cached = JSON.parse(cachedRaw) as { email?: string; customerId?: string; userId?: string; name?: string };
          if (cached.name) {
            this.customerName.set(cached.name);
            return;
          }
          if (cached.customerId) {
            const token = await this.auth.getIdToken();
            const headers: Record<string, string> = { 'Content-Type': 'application/json' };
            if (token) headers['Authorization'] = token;
            const res = await firstValueFrom(
              this.http.get<{ name?: string }>(`${CASTOR_API_URL}/customers/${cached.customerId}`, { headers })
            );
            const name = (res?.name ?? '').trim();
            if (name) {
              this.customerName.set(name);
              sessionStorage.setItem(
                'castor.runner.customer',
                JSON.stringify({ ...cached, name }),
              );
            }
            return;
          }
        } catch {
          // ignore parse errors and refetch
        }
      }

      const email = await this.auth.getEmail();
      if (!email) return;

      const customer = await this.customerLookup.getByEmail(email);
      if (customer?.name) {
        this.customerName.set(customer.name);
        sessionStorage.setItem(
          'castor.runner.customer',
          JSON.stringify({
            email: customer.email ?? email,
            customerId: customer.customerId,
            name: customer.name,
          }),
        );
      }
    } catch {
      // leave customerName as-is (empty or previously cached)
    }
  }

  private loadAdminMode(): void {
    const raw = sessionStorage.getItem(MainLayoutComponent.ADMIN_MODE_KEY);
    if (raw === '0') {
      this.adminMode.set(false);
    } else {
      this.adminMode.set(true);
    }
  }

  private async loadFormsForMenuIfNeeded(): Promise<void> {
    if (this.adminMode()) {
      return;
    }

    try {
      let customerId: string | undefined;
      const cachedRaw = sessionStorage.getItem('castor.runner.customer');
      if (cachedRaw) {
        try {
          const cached = JSON.parse(cachedRaw) as { customerId?: string };
          customerId = cached.customerId;
        } catch {
          // ignore parse errors and fall back to no customerId filter
        }
      }

      const cruds = await this.marketplace.listAccessibleCruds(customerId);
      this.formsForMenu.set(cruds);
    } catch {
      // leave formsForMenu empty on error
    }
  }

  toggleTheme(): void {
    this.themeService.toggle();
  }

  setLanguage(lang: 'pt' | 'en'): void {
    this.languageService.setLanguage(lang);
    const locale = lang === 'pt' ? 'pt-BR' : 'en';
    this.i18n.setLocale(locale);
  }

  async logout(): Promise<void> {
    sessionStorage.removeItem('castor.runner.customer');
    sessionStorage.setItem(MainLayoutComponent.ADMIN_MODE_KEY, '0');
    await this.auth.signOut();
    this.router.navigate(['/login']);
  }
}
